import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { fetchTenants, fetchPortalUsers, createTenant, createPortalUser, updateTenant, deleteTenant } from '../../services/pocketbase';
import { CLIENT_LOGIN_URL, PUBLIC_SITE_BASE_URL, isPlatformAdminRole, isSuperAdminRole } from '../../config/constants';
import { format } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

// Replaced FiBuilding with FiBriefcase
const {
  FiPlus, FiBriefcase, FiUsers, FiX, FiSave, FiEye, FiSearch, FiCopy, FiMail,
  FiCheckCircle, FiInfo, FiExternalLink, FiGlobe, FiClock, FiMapPin, FiPhone,
  FiEdit2, FiMoreVertical, FiArchive, FiRotateCcw, FiTrash2, FiAlertTriangle,
  FiFilter, FiCheck, FiSlash, FiChevronDown, FiChevronUp, FiClipboard, FiCheckSquare, FiSend
} = FiIcons;

const ONBOARDING_STORAGE_KEY = 'bpas_tenant_onboarding_v1';
const ONBOARDING_MANUAL_DEFAULTS = {
  website_deployed: false,
  enquiry_tested: false,
  welcome_email_sent: false
};

const AdminTenants = () => {
  const { startImpersonation, currentUserRole } = useAuth();
  const isPlatformAdmin = isPlatformAdminRole(currentUserRole);
  const isSuperAdmin = isSuperAdminRole(currentUserRole);
  const [tenants, setTenants] = useState([]);
  const [tenantOwners, setTenantOwners] = useState({});
  const [onboardingTenant, setOnboardingTenant] = useState(null);
  const [onboardingByTenant, setOnboardingByTenant] = useState(() => {
    try {
      const raw = window.localStorage.getItem(ONBOARDING_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter State
  const [statusFilter, setStatusFilter] = useState('active');

  // Menu State
  const [openMenuId, setOpenMenuId] = useState(null);

  // Email Helper State
  const [emailModalData, setEmailModalData] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');

  // Tenant Details State
  const [viewingTenant, setViewingTenant] = useState(null);
  const [detailsFormData, setDetailsFormData] = useState({ public_site_url: '', notes: '' });
  const [isSavingDetails, setIsSavingDetails] = useState(false);

  // Archive Modal State
  const [archiveModal, setArchiveModal] = useState({ isOpen: false, step: 1, tenant: null, typedName: '' });
  const [isArchiving, setIsArchiving] = useState(false);

  // Unarchive Modal State
  const [unarchiveModal, setUnarchiveModal] = useState({ isOpen: false, tenant: null });
  const [isUnarchiving, setIsUnarchiving] = useState(false);

  // Delete Confirmation State
  const [deleteData, setDeleteData] = useState(null);
  const [deleteInput, setDeleteInput] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // New Tenant Form State
  const [formData, setFormData] = useState({
    business_name: '',
    tenant_slug: '',
    email: '',
    phone: '',
    address: '',
    website_url: '',
    admin_user_email: '',
    admin_user_name: '',
    initial_password: ''
  });
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);

  useEffect(() => {
    fetchTenantsData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && !event.target.closest('.action-menu-container')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const fetchTenantsData = async () => {
    try {
      setIsLoading(true);
      const tenantData = await fetchTenants();
      setTenants(Array.isArray(tenantData) ? tenantData : []);

      try {
        const userData = await fetchPortalUsers();
        const owners = {};
        (Array.isArray(userData) ? userData : []).forEach((user) => {
          const tenantId = typeof user.tenant === 'string' ? user.tenant : user.tenant?.id;
          if (tenantId && user.role === 'owner' && !owners[tenantId]) owners[tenantId] = user;
        });
        setTenantOwners(owners);
      } catch (userErr) {
        console.warn('Tenant owners could not be loaded:', userErr);
        setTenantOwners({});
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const generateSlug = (businessName) => {
    return businessName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const generatePassword = () => {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    const bytes = new Uint32Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, value => alphabet[value % alphabet.length]).join('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    setError('');
    setSuccess('');

    try {
      const slug = generateSlug(formData.tenant_slug || formData.business_name);
      if (!slug) {
        throw new Error('A valid tenant slug is required.');
      }

      const tenantData = {
        business_name: formData.business_name.trim(),
        tenant_slug: slug,
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        website_url: formData.website_url.trim(),
        public_site_url: `${PUBLIC_SITE_BASE_URL}/${slug}`,
        is_archived: false,
        notes: ''
      };

      const newTenant = await createTenant(tenantData);

      const password = formData.initial_password || generatePassword();
      const userData = {
        email: formData.admin_user_email.trim(),
        password: password,
        passwordConfirm: password,
        display_name: formData.admin_user_name.trim(),
        role: 'owner',
        tenant: newTenant.id
      };

      const newOwner = await createPortalUser(userData);
      setTenants([newTenant, ...tenants]);
      setTenantOwners(prev => ({ ...prev, [newTenant.id]: newOwner }));
      setSuccess('Client created successfully.');

      setEmailModalData({
        tenantId: newTenant.id,
        businessName: formData.business_name,
        adminName: formData.admin_user_name,
        adminEmail: formData.admin_user_email,
        tempPassword: password,
        loginUrl: CLIENT_LOGIN_URL
      });
      setIsEmailModalOpen(true);
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      business_name: '',
      tenant_slug: '',
      email: '',
      phone: '',
      address: '',
      website_url: '',
      admin_user_email: '',
      admin_user_name: '',
      initial_password: ''
    });
    setIsSlugManuallyEdited(false);
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'business_name') {
      setFormData(prev => ({
        ...prev,
        business_name: value,
        tenant_slug: isSlugManuallyEdited ? prev.tenant_slug : generateSlug(value)
      }));
    } else if (name === 'tenant_slug') {
      setIsSlugManuallyEdited(true);
      setFormData(prev => ({ ...prev, tenant_slug: generateSlug(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (error) setError('');
  };

  const handleImpersonate = (tenant) => {
    if (tenant.is_archived) {
      setError("Cannot impersonate an archived tenant.");
      return;
    }
    startImpersonation(tenant.id, tenant.business_name);
    window.location.hash = '#/dashboard';
  };

  const initiateArchive = (tenant) => {
    setArchiveModal({ isOpen: true, step: 1, tenant: tenant, typedName: '' });
    setOpenMenuId(null);
  };

  const handleArchiveStep1 = () => {
    setArchiveModal(prev => ({ ...prev, step: 2 }));
  };

  const handleArchiveFinal = async () => {
    const { tenant, typedName } = archiveModal;
    if (!tenant) return;
    if (typedName.trim() !== tenant.business_name.trim()) return;

    setIsArchiving(true);
    try {
      const updated = await updateTenant(tenant.id, { is_archived: true });
      setTenants(tenants.map(t => t.id === tenant.id ? updated : t));
      setSuccess(`Tenant "${tenant.business_name}" has been archived.`);
      setArchiveModal({ isOpen: false, step: 1, tenant: null, typedName: '' });
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError("Failed to archive tenant: " + err.message);
    } finally {
      setIsArchiving(false);
    }
  };

  const closeArchiveModal = () => {
    setArchiveModal({ isOpen: false, step: 1, tenant: null, typedName: '' });
  };

  const initiateUnarchive = (tenant) => {
    setUnarchiveModal({ isOpen: true, tenant: tenant });
    setOpenMenuId(null);
  };

  const handleUnarchiveFinal = async () => {
    const { tenant } = unarchiveModal;
    if (!tenant) return;

    setIsUnarchiving(true);
    try {
      const updated = await updateTenant(tenant.id, { is_archived: false });
      setTenants(tenants.map(t => t.id === tenant.id ? updated : t));
      setSuccess(`Tenant "${tenant.business_name}" unarchived.`);
      setUnarchiveModal({ isOpen: false, tenant: null });
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError("Failed to unarchive tenant: " + err.message);
    } finally {
      setIsUnarchiving(false);
    }
  };

  const initiateDelete = (tenant) => {
    setDeleteData(tenant);
    setDeleteInput('');
    setOpenMenuId(null);
  };

  const handleDelete = async () => {
    if (!deleteData || deleteInput !== 'DELETE') return;

    setIsDeleting(true);
    try {
      await deleteTenant(deleteData.id);
      setTenants(tenants.filter(t => t.id !== deleteData.id));
      setSuccess(`Tenant "${deleteData.business_name}" has been permanently deleted.`);
      setTimeout(() => setSuccess(''), 3000);
      setDeleteData(null);
    } catch (err) {
      setError("Failed to delete tenant: " + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const persistOnboarding = (next) => {
    setOnboardingByTenant(next);
    try {
      window.localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Local storage is a convenience only; tenant data remains unaffected if unavailable.
    }
  };

  const getOnboardingState = (tenant) => {
    const stored = onboardingByTenant[tenant.id] || ONBOARDING_MANUAL_DEFAULTS;
    return {
      tenant_created: true,
      admin_created: Boolean(tenantOwners[tenant.id]),
      website_deployed: Boolean(stored.website_deployed),
      enquiry_tested: Boolean(stored.enquiry_tested),
      welcome_email_sent: Boolean(stored.welcome_email_sent)
    };
  };

  const getOnboardingProgress = (tenant) => {
    const state = getOnboardingState(tenant);
    const completed = Object.values(state).filter(Boolean).length;
    return { completed, total: 5, percent: Math.round((completed / 5) * 100), state };
  };

  const toggleOnboardingStep = (tenantId, key) => {
    if (!['website_deployed', 'enquiry_tested', 'welcome_email_sent'].includes(key)) return;
    const current = onboardingByTenant[tenantId] || ONBOARDING_MANUAL_DEFAULTS;
    persistOnboarding({
      ...onboardingByTenant,
      [tenantId]: { ...ONBOARDING_MANUAL_DEFAULTS, ...current, [key]: !current[key] }
    });
  };

  const filteredTenants = tenants.filter(tenant => {
    const lowerSearch = searchTerm.toLowerCase();
    const matchesSearch = (tenant.business_name || '').toLowerCase().includes(lowerSearch) || 
                          (tenant.email || '').toLowerCase().includes(lowerSearch) || 
                          (tenant.phone && tenant.phone.toLowerCase().includes(lowerSearch));
    
    let matchesStatus = true;
    if (statusFilter === 'active') {
      matchesStatus = !tenant.is_archived;
    } else if (statusFilter === 'archived') {
      matchesStatus = tenant.is_archived;
    }
    return matchesSearch && matchesStatus;
  });

  const handleViewDetails = (tenant) => {
    setViewingTenant(tenant);
    setDetailsFormData({
      public_site_url: tenant.public_site_url || '',
      notes: tenant.notes || ''
    });
    setOpenMenuId(null);
  };

  const handleCloseDetails = () => {
    setViewingTenant(null);
    setDetailsFormData({ public_site_url: '', notes: '' });
    setCopyFeedback('');
    setIsSavingDetails(false);
  };

  const buildCreatedClientWelcomeEmail = () => {
    if (!emailModalData) return { subject: '', body: '' };
    const greetingName = emailModalData.adminName || emailModalData.businessName || 'there';
    return {
      subject: 'Your BPAS Client Hub login details',
      body: `Hi ${greetingName},

Welcome to BPAS Client Hub for ${emailModalData.businessName}. Your portal is ready.

Login URL: ${emailModalData.loginUrl}
Login email: ${emailModalData.adminEmail}
Temporary password: ${emailModalData.tempPassword}

For security, please log in and change your password when you first access the portal.

Inside the Client Hub you can manage enquiries, add follow-up dates and notes, access help videos, and contact BPA Solutions support.

Regards
BPA Solutions`
    };
  };

  const handleCopyLoginDetails = async () => {
    if (!emailModalData) return;
    const text = `Business: ${emailModalData.businessName}
Login URL: ${emailModalData.loginUrl}
Email: ${emailModalData.adminEmail}
Temporary password: ${emailModalData.tempPassword}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback('Login details copied.');
      setTimeout(() => setCopyFeedback(''), 3000);
    } catch {
      setCopyFeedback('Could not copy.');
    }
  };

  const handleCopyWelcomeEmail = async () => {
    const { body } = buildCreatedClientWelcomeEmail();
    if (!body) return;
    try {
      await navigator.clipboard.writeText(body);
      setCopyFeedback('Welcome email copied.');
      setTimeout(() => setCopyFeedback(''), 3000);
    } catch {
      setCopyFeedback('Could not copy.');
    }
  };

  const handleOpenEmailDraft = () => {
    if (!emailModalData?.adminEmail) return;
    const { subject, body } = buildCreatedClientWelcomeEmail();
    window.location.href = `mailto:${emailModalData.adminEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleCloseEmailModal = () => {
    setIsEmailModalOpen(false);
    setEmailModalData(null);
    setCopyFeedback('');
  };

  const handleDetailsChange = (e) => {
    const { name, value } = e.target;
    setDetailsFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveDetails = async () => {
    if (!viewingTenant) return;
    setIsSavingDetails(true);
    try {
      const updatedTenant = await updateTenant(viewingTenant.id, detailsFormData);
      setTenants(tenants.map(t => t.id === viewingTenant.id ? updatedTenant : t));
      setViewingTenant(updatedTenant);
      setCopyFeedback('Changes saved!');
      setTimeout(() => setCopyFeedback(''), 3000);
    } catch (err) {
      setCopyFeedback('Failed to save.');
    } finally {
      setIsSavingDetails(false);
    }
  };

  const handleCopySlug = async (slug) => {
    try {
      await navigator.clipboard.writeText(slug);
      setCopyFeedback('Slug copied!');
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (err) {
      setCopyFeedback('Failed to copy');
    }
  };

  const getPublicSiteUrl = (tenant) => {
    if (tenant.public_site_url && tenant.public_site_url.trim() !== '') {
      return tenant.public_site_url;
    }
    return `${PUBLIC_SITE_BASE_URL}/${tenant.tenant_slug}`;
  };

  const handleOpenPublicSite = (tenant) => {
    const url = getPublicSiteUrl(tenant);
    window.open(url, '_blank');
  };

  const handleSendEmailToClient = (tenant) => {
    if (!tenant.email) return;
    const publicSiteUrl = getPublicSiteUrl(tenant);
    const subject = `Website Details for ${tenant.business_name}`;
    const body = `Hi ${tenant.business_name},\n\nHere are your current website details:\n\nPublic Website: ${publicSiteUrl}\nClient Portal: ${CLIENT_LOGIN_URL}\n\nRegards,\nBPAS Solutions`;
    window.location.href = `mailto:${tenant.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleDraftWelcomeEmail = (tenant) => {
    const owner = tenantOwners[tenant?.id];
    const recipient = owner?.email || tenant?.email;
    if (!recipient) return;
    const greetingName = owner?.display_name || tenant.business_name || 'there';
    const subject = 'Welcome to BPA Solutions (BPAS) - Your Client Hub Access';
    const loginEmailLine = owner?.email ? `\nLogin email: ${owner.email}` : '';
    const body = `Hi ${greetingName},

Welcome to BPA Solutions. Your BPAS Client Hub for ${tenant.business_name} is ready.

Client Hub: ${CLIENT_LOGIN_URL}${loginEmailLine}

If you do not know your password, use the password reset option on the login page.

The Help Center contains the quick-start guide and video tutorials, and you can contact support from inside the portal.

Regards,
BPA Solutions`;
    window.location.href = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setOpenMenuId(null);
  };

  if (isLoading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 space-y-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Tenant Management</h1>
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setShowForm(true)} className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">
            <SafeIcon icon={FiPlus} className="w-4 h-4" /> <span>Create New Client</span>
          </motion.button>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6"><p className="text-red-700">{error}</p></div>}
        {success && <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6"><p className="text-green-700">{success}</p></div>}

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            {isPlatformAdmin && (
              <div className="flex items-center space-x-2 bg-gray-100 p-1 rounded-lg self-start">
                <button onClick={() => setStatusFilter('active')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${statusFilter === 'active' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>Active</button>
                <button onClick={() => setStatusFilter('archived')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${statusFilter === 'archived' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>Archived</button>
                <button onClick={() => setStatusFilter('all')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${statusFilter === 'all' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>All</button>
              </div>
            )}
            <div className="relative flex-1 max-w-md">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input type="text" placeholder="Search..." className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-visible">
          {filteredTenants.length === 0 ? (
            <div className="p-8 text-center">
              <SafeIcon icon={FiBriefcase} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">{tenants.length === 0 ? 'No tenants found' : 'No tenants match your filters'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto overflow-y-visible min-h-[300px]">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Website</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" title="Tracks the five client-launch steps for this tenant">Onboarding</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTenants.map((tenant) => (
                    <motion.tr key={tenant.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`${tenant.is_archived ? 'bg-gray-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-6 py-4">
                        <div>
                          <div className="flex items-center">
                            <p className={`text-sm font-medium mr-2 ${tenant.is_archived ? 'text-gray-500' : 'text-gray-900'}`}>{tenant.business_name}</p>
                            {tenant.is_archived ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">Archived</span> : <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Active</span>}
                          </div>
                          <p className="text-xs text-gray-500">/{tenant.tenant_slug}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <p className={`text-sm ${tenant.is_archived ? 'text-gray-500' : 'text-gray-900'}`}>{tenant.email}</p>
                          {tenant.phone && <p className="text-xs text-gray-500">{tenant.phone}</p>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {tenant.public_site_url || tenant.website_url ? (
                          <a href={tenant.public_site_url || tenant.website_url} target="_blank" rel="noopener noreferrer" className={`text-sm truncate max-w-[200px] block ${tenant.is_archived ? 'text-gray-400' : 'text-blue-600 hover:text-blue-500'}`}>
                            {tenant.public_site_url || tenant.website_url}
                          </a>
                        ) : <span className="text-sm text-gray-400">-</span>}
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          const progress = getOnboardingProgress(tenant);
                          return (
                            <button type="button" onClick={() => setOnboardingTenant(tenant)} className="min-w-[120px] text-left group" title="Open tenant onboarding checklist">
                              <div className="flex items-center justify-between text-xs font-semibold text-gray-700 mb-1">
                                <span>{progress.completed}/{progress.total} complete</span>
                                <span>{progress.percent}%</span>
                              </div>
                              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${progress.percent}%` }} />
                              </div>
                            </button>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 relative action-menu-container">
                        <button onClick={() => setOpenMenuId(openMenuId === tenant.id ? null : tenant.id)} className={`p-2 rounded-full hover:bg-gray-100 ${openMenuId === tenant.id ? 'bg-gray-100 text-gray-800' : 'text-gray-400'}`}>
                          <SafeIcon icon={FiMoreVertical} className="w-5 h-5" />
                        </button>
                        <AnimatePresence>
                          {openMenuId === tenant.id && (
                            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="absolute right-8 top-8 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-20 origin-top-right">
                              <div className="py-1">
                                <button onClick={() => handleViewDetails(tenant)} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"><SafeIcon icon={FiInfo} className="w-4 h-4 mr-2" /> View</button>
                                <button onClick={() => handleViewDetails(tenant)} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"><SafeIcon icon={FiEdit2} className="w-4 h-4 mr-2" /> Edit</button>
                                <button onClick={() => { setOnboardingTenant(tenant); setOpenMenuId(null); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center" title="Open the tenant launch checklist"><SafeIcon icon={FiCheckSquare} className="w-4 h-4 mr-2" /> Onboarding Checklist</button>
                                <button onClick={() => handleDraftWelcomeEmail(tenant)} className="w-full text-left px-4 py-2 text-sm text-blue-700 hover:bg-blue-50 flex items-center" title="Draft a reusable portal welcome email"><SafeIcon icon={FiSend} className="w-4 h-4 mr-2" /> Draft Welcome Email</button>
                                {!tenant.is_archived && <button onClick={() => handleImpersonate(tenant)} className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 flex items-center"><SafeIcon icon={FiEye} className="w-4 h-4 mr-2" /> Impersonate</button>}
                              </div>
                              {isPlatformAdmin && (
                                <div className="border-t border-gray-100 py-1">
                                  {tenant.is_archived ? <button onClick={() => initiateUnarchive(tenant)} className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 flex items-center"><SafeIcon icon={FiRotateCcw} className="w-4 h-4 mr-2" /> Unarchive</button> : <button onClick={() => initiateArchive(tenant)} className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 flex items-center"><SafeIcon icon={FiArchive} className="w-4 h-4 mr-2" /> Archive</button>}
                                </div>
                              )}
                              {isSuperAdmin && (
                                <div className="border-t border-gray-100 py-1">
                                  <button onClick={() => initiateDelete(tenant)} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"><SafeIcon icon={FiTrash2} className="w-4 h-4 mr-2" /> Delete</button>
                                </div>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modals omitted for brevity but logic handles them above (Create, Archive, Delete, Details) */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
               <div className="flex justify-between mb-6">
                 <div>
                   <h2 className="text-xl font-bold">Create New Client</h2>
                   <p className="text-sm text-gray-500 mt-1">Create the tenant and its primary client administrator in one controlled step.</p>
                 </div>
                 <button onClick={resetForm} aria-label="Close create client form"><SafeIcon icon={FiX} className="w-5 h-5" /></button>
               </div>
               <form onSubmit={handleSubmit} className="space-y-6">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
                     <input type="text" name="business_name" placeholder="Business Name" required className="w-full border p-2 rounded" value={formData.business_name} onChange={handleChange} />
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1" title="This slug routes public website enquiries to the correct tenant">Tenant Slug *</label>
                     <input type="text" name="tenant_slug" placeholder="business-name" required className="w-full border p-2 rounded font-mono" value={formData.tenant_slug} onChange={handleChange} />
                     <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers and hyphens only. This must match the website tenant slug.</p>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">Business Email *</label>
                     <input type="email" name="email" placeholder="Business Email" required className="w-full border p-2 rounded" value={formData.email} onChange={handleChange} />
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                     <input type="tel" name="phone" placeholder="Phone Number" className="w-full border p-2 rounded" value={formData.phone} onChange={handleChange} />
                   </div>
                   <div className="md:col-span-2">
                     <label className="block text-sm font-medium text-gray-700 mb-1">Business Address</label>
                     <input type="text" name="address" placeholder="Business Address" className="w-full border p-2 rounded" value={formData.address} onChange={handleChange} />
                   </div>
                   <div className="md:col-span-2">
                     <label className="block text-sm font-medium text-gray-700 mb-1">Existing Website URL</label>
                     <input type="url" name="website_url" placeholder="https://example.com" className="w-full border p-2 rounded" value={formData.website_url} onChange={handleChange} />
                   </div>
                 </div>

                 <div className="border-t border-gray-200 pt-5">
                   <h3 className="font-semibold text-gray-900 mb-1">Primary Client Administrator</h3>
                   <p className="text-xs text-gray-500 mb-4">This account will be linked to the new tenant automatically.</p>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Admin Name *</label>
                       <input type="text" name="admin_user_name" placeholder="Full Name" required className="w-full border p-2 rounded" value={formData.admin_user_name} onChange={handleChange} />
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email *</label>
                       <input type="email" name="admin_user_email" placeholder="Admin User Email" required className="w-full border p-2 rounded" value={formData.admin_user_email} onChange={handleChange} />
                     </div>
                     <div className="md:col-span-2">
                       <label className="block text-sm font-medium text-gray-700 mb-1" title="Leave blank to generate a cryptographically random temporary password">Initial Password</label>
                       <input type="password" name="initial_password" placeholder="Leave blank to generate a secure temporary password" className="w-full border p-2 rounded" value={formData.initial_password} onChange={handleChange} autoComplete="new-password" />
                       <p className="text-xs text-gray-500 mt-1">If left blank, a secure temporary password is generated once and shown after creation.</p>
                     </div>
                   </div>
                 </div>

                 <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
                   <button type="button" onClick={resetForm} className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50">Cancel</button>
                   <button type="submit" disabled={isCreating} className="bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700 disabled:opacity-50">{isCreating ? 'Creating...' : 'Create Client'}</button>
                 </div>
               </form>
            </div>
          </div>
        )}
        
        {/* Welcome Email Modal - shown immediately after tenant + owner creation */}
        {isEmailModalOpen && emailModalData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex items-start justify-between bg-gray-50">
                <div>
                  <div className="flex items-center text-green-700 font-bold mb-1"><SafeIcon icon={FiCheckCircle} className="w-5 h-5 mr-2" /> Client created successfully</div>
                  <h2 className="text-xl font-bold text-gray-900">Welcome Email & Login Details</h2>
                  <p className="text-sm text-gray-500 mt-1">Copy these credentials before closing. The temporary password is intentionally only surfaced at creation time.</p>
                </div>
                <button onClick={handleCloseEmailModal} aria-label="Close welcome email modal"><SafeIcon icon={FiX} className="w-5 h-5 text-gray-500" /></button>
              </div>
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm">
                  <div><span className="block text-xs font-bold text-blue-500 uppercase">Business</span><span className="font-semibold text-blue-950">{emailModalData.businessName}</span></div>
                  <div><span className="block text-xs font-bold text-blue-500 uppercase">Client admin</span><span className="font-semibold text-blue-950">{emailModalData.adminName || emailModalData.adminEmail}</span></div>
                  <div><span className="block text-xs font-bold text-blue-500 uppercase">Login email</span><span className="font-mono text-blue-950 break-all">{emailModalData.adminEmail}</span></div>
                  <div><span className="block text-xs font-bold text-blue-500 uppercase">Temporary password</span><span className="font-mono text-blue-950 break-all">{emailModalData.tempPassword}</span></div>
                  <div className="sm:col-span-2"><span className="block text-xs font-bold text-blue-500 uppercase">Client Hub</span><span className="font-mono text-blue-950 break-all">{emailModalData.loginUrl}</span></div>
                </div>
                {copyFeedback && <div className="text-sm font-medium text-green-700 bg-green-50 border border-green-100 rounded p-3">{copyFeedback}</div>}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <button onClick={handleCopyLoginDetails} className="px-4 py-2.5 border border-gray-300 rounded-md text-sm font-bold text-gray-700 hover:bg-gray-50 flex items-center justify-center" title="Copy the URL, login email and temporary password"><SafeIcon icon={FiClipboard} className="w-4 h-4 mr-2" /> Copy Login Details</button>
                  <button onClick={handleCopyWelcomeEmail} className="px-4 py-2.5 border border-blue-200 bg-blue-50 rounded-md text-sm font-bold text-blue-700 hover:bg-blue-100 flex items-center justify-center" title="Copy the complete welcome email body"><SafeIcon icon={FiCopy} className="w-4 h-4 mr-2" /> Copy Welcome Email</button>
                  <button onClick={handleOpenEmailDraft} className="px-4 py-2.5 bg-blue-600 text-white rounded-md text-sm font-bold hover:bg-blue-700 flex items-center justify-center" title="Open the client admin email in your default mail app"><SafeIcon icon={FiMail} className="w-4 h-4 mr-2" /> Open Email Draft</button>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                <button onClick={handleCloseEmailModal} className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-gray-900">Done</button>
              </div>
            </div>
          </div>
        )}

        {/* Per-tenant onboarding checklist. Manual completion is browser-local by design. */}
        {onboardingTenant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[55] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full p-6">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Tenant Onboarding Checklist</h2>
                  <p className="text-sm text-gray-500 mt-1">{onboardingTenant.business_name}</p>
                </div>
                <button onClick={() => setOnboardingTenant(null)} aria-label="Close onboarding checklist"><SafeIcon icon={FiX} className="w-5 h-5" /></button>
              </div>
              {(() => {
                const progress = getOnboardingProgress(onboardingTenant);
                const steps = [
                  { key: 'tenant_created', title: 'Tenant created', detail: 'Tenant record exists in BPAS Client Hub.', automatic: true },
                  { key: 'admin_created', title: 'Client admin created', detail: tenantOwners[onboardingTenant.id] ? `Owner login: ${tenantOwners[onboardingTenant.id].email}` : 'No owner account was detected for this tenant.', automatic: true },
                  { key: 'website_deployed', title: 'Website deployed and slug verified', detail: `Confirm the public website uses tenant_slug: ${onboardingTenant.tenant_slug}` },
                  { key: 'enquiry_tested', title: 'Test enquiry received', detail: 'Submit a test website form and confirm it appears for this tenant.' },
                  { key: 'welcome_email_sent', title: 'Welcome email sent', detail: 'Send the client their portal access and Help Center instructions.' }
                ];
                return (
                  <>
                    <div className="mb-5">
                      <div className="flex justify-between text-sm font-semibold text-gray-700 mb-2"><span>{progress.completed} of 5 complete</span><span>{progress.percent}%</span></div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-blue-600 transition-all" style={{ width: `${progress.percent}%` }} /></div>
                    </div>
                    <div className="space-y-3">
                      {steps.map(step => {
                        const checked = progress.state[step.key];
                        return (
                          <label key={step.key} className={`flex items-start gap-3 p-3 rounded-lg border ${checked ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'} ${step.automatic ? 'cursor-default' : 'cursor-pointer hover:bg-gray-50'}`}>
                            <input type="checkbox" checked={checked} disabled={step.automatic} onChange={() => toggleOnboardingStep(onboardingTenant.id, step.key)} className="mt-1 rounded text-blue-600" />
                            <span className="flex-1"><span className="block text-sm font-bold text-gray-900">{step.title}{step.automatic ? ' (automatic)' : ''}</span><span className="block text-xs text-gray-500 mt-1">{step.detail}</span></span>
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-4">Manual checklist ticks are stored in this browser only. No PocketBase schema fields are added or modified.</p>
                  </>
                );
              })()}
              <div className="flex justify-end mt-5"><button onClick={() => setOnboardingTenant(null)} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-bold hover:bg-blue-700">Done</button></div>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {viewingTenant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
             <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
                <div className="flex justify-between mb-4">
                  <h2 className="text-xl font-bold">Tenant Details</h2>
                  <button onClick={handleCloseDetails}><SafeIcon icon={FiX} className="w-5 h-5" /></button>
                </div>
                <div className="space-y-4">
                   <p><strong>Business:</strong> {viewingTenant.business_name}</p>
                   <p><strong>Slug:</strong> {viewingTenant.tenant_slug}</p>
                   <div>
                     <label className="block text-sm font-bold">Public Site URL</label>
                     <input type="text" name="public_site_url" value={detailsFormData.public_site_url} onChange={handleDetailsChange} className="w-full border p-2 rounded" />
                   </div>
                   <div>
                     <label className="block text-sm font-bold">Internal Notes</label>
                     <textarea name="notes" value={detailsFormData.notes} onChange={handleDetailsChange} className="w-full border p-2 rounded" rows={3} />
                   </div>
                   <button onClick={handleSaveDetails} disabled={isSavingDetails} className="w-full bg-blue-600 text-white py-2 rounded">{isSavingDetails ? 'Saving...' : 'Save Changes'}</button>
                </div>
             </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminTenants;